import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useQueryPagination from 'hooks/useQueryPagination'
import { AppDispatch, RootState } from 'store'
import { EditContainer, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { InventoryTransferModal } from './InventoryTransferModal'
import {
  clearInventoryTransfers,
  getInventoryTransfers,
} from 'features/inventory/inventoryTransfersSlice'
import { confirmInventoryTransferReceipt } from 'features/inventory/inventoryControlSlice'
import { ViewInventoryTransferModal } from './ViewInventoryTransferModal'
import { ArrowLeft } from 'assets/icons/ArrowLeft'
import { IInventoryTransferRow } from 'types/inventoryControl.interface'
import { InventoryTransferStatus } from 'types/inventoryEnums.interface'
import { TRANSFER_STATUS_META } from '../Shared/inventoryEnumMaps'
import { TRANSFER_RECEIPT_OK_NOTE } from '../Shared/inventoryFormatters'
import { TransferRefText } from './InventoryTransfers.styled'

const toTransferRow = (row: IInventoryTransferRow): IInventoryTransferRow => {
  const status =
    row.status ??
    row.control?.status ??
    InventoryTransferStatus.Dispatched
  return {
    ...row,
    control: {
      status,
      dispatchedAt: row.dispatchedAt ?? row.control?.dispatchedAt ?? row.date ?? null,
      receivedAt: row.receivedAt ?? row.control?.receivedAt ?? null,
      receivedBy: row.receivedBy ?? row.control?.receivedBy ?? null,
      varianceNote: row.varianceNote ?? row.control?.varianceNote ?? null,
    },
  }
}

export const InventoryTransfers = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { transfers, totalPages } = useSelector((store: RootState) => store.inventoryTransfers)
  const [params, setParams] = useState<any>(getParams({ location }))
  const [modal, setModal] = useState(false)
  const [viewId, setViewId] = useState<number | null>(null)

  useQueryPagination({
    fetchFunctions: [getInventoryTransfers],
    cleanUpFunctions: [clearInventoryTransfers],
    params,
  })

  const rows: IInventoryTransferRow[] = useMemo(() => {
    const seen: { [id: number]: boolean } = {}
    return (transfers ?? [])
      .map(toTransferRow)
      .filter((row) => {
        if (seen[row.id]) return false
        seen[row.id] = true
        return true
      })
  }, [transfers])

  const viewRow = rows.find((row) => row.id === viewId) || null

  const handleConfirmReceipt = async (row: IInventoryTransferRow) => {
    const resp = await dispatch(
      confirmInventoryTransferReceipt({
        id: row.id,
        body: { varianceNote: TRANSFER_RECEIPT_OK_NOTE },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setViewId(null)
      dispatch(getInventoryTransfers(params))
    }
  }

  const columns: IHonestTableColumn<IInventoryTransferRow>[] = [
    {
      id: 'ref',
      header: 'REF',
      cell: (item) => <TransferRefText>{`TRF-${item.id}`}</TransferRefText>,
    },
    { id: 'date', header: 'DATE', cell: (item) => formatDateForDisplay(item.date, true) },
    {
      id: 'assignedEmployee',
      header: 'ASSIGNED EMPLOYEE',
      cell: (item) =>
        item.assignedForUser
          ? `${item.assignedForUser.firstName} ${item.assignedForUser.lastName}`
          : '/',
    },
    {
      id: 'fromLocation',
      header: 'FROM LOCATION',
      cell: (item) => (item.fromLocation ? item.fromLocation.name : '/'),
    },
    {
      id: 'toLocation',
      header: 'TO LOCATION',
      cell: (item) => (item.toLocation ? item.toLocation.name : '/'),
    },
    { id: 'items', header: 'ITEMS', cell: (item) => item.lines.length || '/' },
    {
      id: 'status',
      header: 'STATUS',
      cell: (item) => {
        if (!item.control) return <OtherText grey>—</OtherText>
        const meta = TRANSFER_STATUS_META[item.control.status] ?? {
          label: String(item.control.status),
          tone: 'neutral' as const,
        }
        return (
          <StatusPill
            status={meta.tone}
            dot={item.control.status === InventoryTransferStatus.InTransit}
          >
            {meta.label}
          </StatusPill>
        )
      },
    },
    { id: 'notes', header: 'NOTES', cell: (item) => item.notes || '/' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (item) => (
        <span onClick={(e) => e.stopPropagation()}>
          <EditContainer onClick={() => setViewId(item.id)}>
            <Flex style={{ transform: 'rotate(180deg)' }} className='arrow-table'>
              <ArrowLeft />
            </Flex>
          </EditContainer>
        </span>
      ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='16'>
          <MainHeadline>Transfers</MainHeadline>
          <Flex gap='1rem'>
            <Button variant='primary' onClick={() => setModal(true)}>
              Add Transfer
            </Button>
          </Flex>
        </Flex>
        {!rows.length ? (
          <NoItems title='Transfers' />
        ) : (
          <HonestTable
            columns={columns}
            data={rows}
            keyField='id'
            onClick={(item) => setViewId(item.id)}
          />
        )}
        {modal && <InventoryTransferModal closeModal={() => setModal(false)} />}
        {viewRow && (
          <ViewInventoryTransferModal
            value={viewRow}
            closeModal={() => setViewId(null)}
            onConfirmReceipt={handleConfirmReceipt}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
